import { Fragment, useEffect, useState } from "react";
import { createStyles, Space, Text, ActionIcon, Timeline, Textarea } from "@mantine/core";
import { IconX, IconSearch } from "@tabler/icons-react";
import { useJuneProvider } from "../../contexts/analytics.context";

const useStyles = createStyles((theme) => ({

    formWrapper: {
        // background:'pink',
        padding: '0px 25px',
        paddingRight: '0px',
        width: '285px',
        overflowX: 'hidden',
        overflowY: 'scroll',
        height: 'calc(100vh - 120px)',
        '&::-webkit-scrollbar': {
            backgroundColor: 'transparent',
            transform: 'translateY(-100px)',
            'width': '8px',
        },
        borderRight: '2px solid #f1f1f1',
        '&:hover': {
            '&::-webkit-scrollbar-thumb': window.ontouchstart === undefined && {
                borderRadius: '8px',
                backgroundColor: 'rgba(0,0,0,0.5)',
            }
        },
        // If window width is less than 1000px
        '@media (max-width: 1250px)': {
            position: 'absolute',
            top: '120px',
            left: '0px',
            zIndex: '1',
            background: 'white',
        },
        [theme.fn.smallerThan('sm')]: {
            display: 'none',
        },
    },

    timelineItem: {
        padding: '0px',
        cursor: 'pointer',
        height: '70px',
        marginTop: '0px !important',
        borderLeft: `2px solid ${theme.colors.gray[3]}`,
        position: 'relative',

        '&::before': {
            display: 'none !important'
        },

        '.mantine-Timeline-itemBullet': {
            border: `2px solid ${theme.colors.gray[3]}`,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            msTransform: 'translateY(-50%)',
            MozTransform: 'translateY(-50%)',
            WebkitTransform: 'translateY(-50%)',
            OTransform: 'translateY(-50%)',
        },

        '.mantine-Text-root': {
            color: theme.colors.gray[6],
            lineHeight: '1.2',
            padding: '2px 15px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: '2',
            WebkitBoxOrient: 'vertical',
        },

        '&:hover': {
            backgroundColor: theme.colors.gray[0],
        },

        '.mantine-Timeline-itemBody': {
            position: 'absolute',
            'top': '50%',
            transform: 'translateY(-50%)',
            msTransform: 'translateY(-50%)',
            MozTransform: 'translateY(-50%)',
            WebkitTransform: 'translateY(-50%)',
            OTransform: 'translateY(-50%)',
        }

    },

    timelineItemSelected: {
        borderLeft: `2px solid ${theme.colors.gray[8]}`,

        '&:hover': {
            backgroundColor: theme.colors.gray[1],
        },

        '.mantine-Timeline-itemBullet': {
            border: `2px solid ${theme.colors.gray[8]}`,
        },

        '.mantine-Text-root': {
            color: theme.colors.dark,
        },
    },

    capitalizedTextWrapper: {
        textTransform: 'uppercase',
        fontWeight: '500',
        fontSize: theme.fontSizes.md,
        position: 'sticky',
        zIndex: '2',
        top: '0px',
        background: 'white',
        height: '55px',
        display: 'flex',
        alignItems: 'center',
    },

    alertText: {
        lineHeight: 1.3
    },

    searchBar: {
        marginRight: '10px',
        '.mantine-Input-rightSection': {
            marginRight: '5px',
        }
    },

    closeIconWrapper: {
        position: 'fixed',
        width: '32px',
        left: '233px',
        zIndex: '3',
        height: '55px',
        display: 'flex',
        alignItems: 'center',
        background: 'white',
    },

    searchCrossIcon: {
        marginRight: '7.5px'
    }

}));

const convertTimestampToTime = (timestamp) => {
    const date = new Date(timestamp);
    const hours24 = date.getHours();
    const hours = hours24 % 12 || 12;
    const ampm = hours24 >= 12 ? 'pm' : 'am';
    const minutes = date.getMinutes();
    return `${hours}:${minutes.toString().padStart(2, '0')}${ampm}`;
}

const dateToLocalDay = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}


const getUniqueDays = (userSurveyHistory) => {
    const dates = [];
    userSurveyHistory.forEach((survey) => {
        const date = new Date(survey.date);
        const dayString = dateToLocalDay(date);
        if (!dates.includes(dayString)) dates.push(dayString);
    });
    return dates;
}

const generateSurveyHistoryDictionary = (userSurveyHistory) => {
    const uniqueDays = getUniqueDays(userSurveyHistory);
    const surveyHistoryDictionary = {};
    uniqueDays.forEach((day) => {
        surveyHistoryDictionary[day] = [];
    }
    );
    userSurveyHistory.forEach((survey) => {
        const date = new Date(survey.date);
        const dayString = dateToLocalDay(date);
        surveyHistoryDictionary[dayString].push(survey);
    }
    );
    return surveyHistoryDictionary;
}

const getShownSurveys = (structuredSurveyHistory, searchString) => {
    if (searchString === '') return structuredSurveyHistory;
    const shownSurveys = {};
    const lowerCaseSearchString = searchString.toLowerCase();
    Object.keys(structuredSurveyHistory).forEach((day) => {
        const surveys = structuredSurveyHistory[day];
        const filteredSurveys = surveys.filter((survey) => {
            if (survey.surveyQuestion.toLowerCase().includes(lowerCaseSearchString)) return true;
            if (survey.hasOwnProperty('surveyDescriptions') && survey.surveyDescriptions.length > 0) {
                for (let description of survey.surveyDescriptions) {
                    if (description.toLowerCase().includes(lowerCaseSearchString)) return true;
                }
            }
            if (survey.hasOwnProperty('surveyAnswers')) {
                for (let answer of survey.surveyAnswers) {
                    if (answer.toLowerCase().includes(lowerCaseSearchString)) return true;
                }
            }
            if (survey.customQuestionsAndResponses.length > 0) {
                for (let customQuestionAndResponse of survey.customQuestionsAndResponses) {
                    if (customQuestionAndResponse.question.toLowerCase().includes(lowerCaseSearchString)) return true;
                    if (customQuestionAndResponse.response.toLowerCase().includes(lowerCaseSearchString)) return true;
                    if (customQuestionAndResponse.options.length > 0) {
                        for (let option of customQuestionAndResponse.options) {
                            if (option.toLowerCase().includes(lowerCaseSearchString)) return true;
                        }
                    }
                }
            }
            if (survey.hasOwnProperty('gaborGrangerProductName')) {
                if (survey.gaborGrangerProductName.toLowerCase().includes(lowerCaseSearchString)) return true;
            }
            if (survey.hasOwnProperty('gaborGrangerProductDescription')) {
                if (survey.gaborGrangerProductDescription.toLowerCase().includes(lowerCaseSearchString)) return true;
            }
            return false;
        });
        if (filteredSurveys.length > 0) shownSurveys[day] = filteredSurveys;
    }
    );
    return shownSurveys;
}


const SurveyHistory = ({ surveyResponse, setSurveyResponse, historyShown, setHistoryShown, userSurveyHistory, currentUser, loadingText, setExampleSearchValue, userSurveyHistoryLoaded }) => {


    const [selectedSurveyID, setSelectedSurveyID] = useState('');
    const [searchString, setSearchString] = useState('');
    const [structuredSurveyHistory, setStructuredSurveyHistory] = useState({});
    const [shownSurveys, setShownSurveys] = useState({});

    const analytics = useJuneProvider();

    useEffect(() => {
        // Check if empty
        const surveyId = (Object.keys(surveyResponse).length === 0) ? '' : surveyResponse.id;
        setSelectedSurveyID(surveyId);
    }, [surveyResponse])

    useEffect(() => {
        const structuredHistory = generateSurveyHistoryDictionary(userSurveyHistory);
        setStructuredSurveyHistory(structuredHistory);
        setShownSurveys(structuredHistory);
    }, [userSurveyHistory]);

    useEffect(() => {
        setShownSurveys(getShownSurveys(structuredSurveyHistory, searchString));
    }, [searchString, structuredSurveyHistory]);

    useEffect(() => {
        if (historyShown === false) setSearchString('');
    }, [historyShown])

    const { classes, cx } = useStyles();

    if (!historyShown) return null;

    return (
        <div className={classes.formWrapper}>
            <div className={classes.closeIconWrapper}>
                <ActionIcon
                    className={classes.closeIcon}
                    size={32}
                    // variant='transparent'
                    onMouseDown={() => { setHistoryShown(!historyShown) }}
                >

                    <IconX size={20} stroke={1.5} />
                </ActionIcon>
            </div>
            <div className={classes.capitalizedTextWrapper}>
                <Text size='sm'>
                    Survey History
                </Text>
            </div>
            {
                Object.keys(userSurveyHistory).length === 0
                    ?
                    userSurveyHistoryLoaded ?
                        <>
                            <Space h='md' />
                            <Text size='md'>No surveys found...</Text>
                        </>
                        :
                        <>
                            <Space h='md' />
                            <Text size='md'>{loadingText}</Text>
                        </>

                    :
                    <>
                        <Text className={classes.alertText} size='xs'>
                            <Text
                                className={classes.alertText}
                                component='span'
                                color='#fa5252'
                                weight='600'
                            >Note: </Text>Re-running surveys loaded from history may give slightly different results.
                        </Text>
                        <Space h='md' />
                        <Textarea
                            className={classes.searchBar}
                            placeholder='Search your surveys...'
                            size='sm'
                            autosize
                            value={searchString}
                            onChange={e => { setSearchString(e.target.value) }}
                            maxLength='500'
                            radius='lg'
                            icon={<IconSearch size={20} stroke={1.5} />}
                            rightSection={
                                <ActionIcon
                                    hidden={searchString === ''}
                                    size={32}
                                    radius="xl"
                                    className={classes.searchCrossIcon}
                                    onMouseDown={() => { setSearchString('') }}
                                >
                                    <IconX size={20} stroke={1.5} />
                                </ActionIcon>
                            }
                        />
                        {
                            Object.keys(shownSurveys).length === 0
                                ?
                                <>
                                    <Space h='md' />
                                    <Text size='md'>No surveys found...</Text>

                                </>
                                :
                                Object.keys(shownSurveys).map(day =>
                                    <Fragment key={day}>
                                        <div className={classes.capitalizedTextWrapper}>
                                            <Text size='sm'>{day}</Text>
                                        </div>
                                        <Timeline
                                            radius="xs"
                                            active={-1}
                                            lineWidth={1}
                                            bulletSize={10}
                                        >
                                            {shownSurveys[day] && shownSurveys[day].map((survey) => (
                                                <Timeline.Item
                                                    key={survey.id}
                                                    onClick={() => {
                                                        if (selectedSurveyID === survey.id) {
                                                            setSurveyResponse({});
                                                            return;
                                                        }
                                                        setSurveyResponse(survey);
                                                        setExampleSearchValue('');
                                                        if (analytics) {
                                                            analytics.track({
                                                                event: 'Loaded sandbox survey from history',
                                                                userId: currentUser.uid,
                                                                properties: {
                                                                    surveyId: survey.id,
                                                                }
                                                            });
                                                        }
                                                    }}
                                                    className={cx(classes.timelineItem, { [classes.timelineItemSelected]: selectedSurveyID === survey.id })}
                                                >
                                                    <Text size="sm">{
                                                        survey.surveyType === 'MCQ' || survey.surveyType === undefined ?
                                                            survey.surveyQuestion :
                                                            survey.gaborGrangerProductName ?
                                                                survey.gaborGrangerProductName :
                                                                'Unknown'
                                                    }</Text>
                                                    <Text size="xs" mt={7}>{
                                                        convertTimestampToTime(survey.date)} ▪ {survey.surveyModel} ▪ {
                                                            survey.surveyType === 'MCQ' || survey.surveyType === undefined
                                                                ?
                                                                'Multiple-choice'
                                                                :
                                                                survey.surveyType === 'GGS'
                                                                    ?
                                                                    'Gabor-Granger'
                                                                    :
                                                                    'Unknown type'
                                                        }
                                                    </Text>
                                                </Timeline.Item>
                                            ))}
                                        </Timeline>
                                    </Fragment>
                                )}
                        <Space h='md' />
                    </>
            }
        </div>
    )
}

export default SurveyHistory;